import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { isConditionBracket, parseConditionString } from '../utils/templateParser.js';
import { scanTextForSyntaxErrors, analyzeConditionText } from '../utils/syntaxValidator.js';

export const smartSyntaxPluginKey = new PluginKey('smartSyntaxAssistant');

/**
 * Checks whether the command text is exact single-angle <page break>
 * without leading < or trailing >
 */
export function isExactPageBreak(text) {
  return /^(?<!<)<page\s+break>(?!>)$/i.test((text || '').trim());
}

/**
 * Checks whether the command text is exact single-angle <line>
 * without leading < or trailing >
 */
export function isExactLine(text) {
  return /^(?<!<)<line>(?!>)$/i.test((text || '').trim());
}


/**
 * SmartSyntaxExtension
 * Implements:
 * 1. Intelligent auto-closing delimiters (<, <<, {, [) & over-typing closing delimiters (>, }, ])
 * 2. Backspace & Delete handling for paired delimiters (<>, <<>>, {}, [])
 * 3. Exact command recognition for <page break> and <line>
 * 4. Strict distinction between single (<...>) and double (<<...>>) angle brackets
 * 5. Full detection of extra/stray delimiters (<, >, {, }, [, ]) and malformed syntax
 * 6. Live condition analysis while user types conditions manually inside [ ... ]
 * 7. Debounced syntax validation & visual indicators with red wavy underline
 */
export const SmartSyntaxExtension = Extension.create({
  name: 'smartSyntaxAssistant',

  addProseMirrorPlugins() {
    let lastEditTimestamp = 0;
    let debounceTimer = null;

    return [
      new Plugin({
        key: smartSyntaxPluginKey,

        state: {
          init() {
            return {
              decorations: DecorationSet.empty,
              lastValidatedDoc: null,
            };
          },

          apply(tr, oldPluginState, oldEditorState, newEditorState) {
            const forceValidate = tr.getMeta(smartSyntaxPluginKey)?.forceValidate;
            const docChanged = tr.docChanged;
            const selectionChanged = !oldEditorState.selection.eq(newEditorState.selection);

            if (docChanged) {
              lastEditTimestamp = Date.now();
            }

            // While user is actively typing, map existing decorations to prevent flicker
            if (docChanged && !forceValidate) {
              return {
                decorations: oldPluginState.decorations.map(tr.mapping, tr.doc),
                lastValidatedDoc: oldPluginState.lastValidatedDoc,
              };
            }

            // If nothing relevant changed and no forceValidate, map existing decorations
            if (!docChanged && !selectionChanged && !forceValidate) {
              return {
                decorations: oldPluginState.decorations.map(tr.mapping, tr.doc),
                lastValidatedDoc: oldPluginState.lastValidatedDoc,
              };
            }

            // Compute fresh validation decorations
            const now = Date.now();
            const cursorPos = newEditorState.selection.from;
            const isSelectionEmpty = newEditorState.selection.empty;
            const decos = [];

            // Scan all text nodes in the document
            newEditorState.doc.descendants((node, pos, parent) => {
              if (!node.isText) return;
              const text = node.text || '';
              const $pos = newEditorState.doc.resolve(pos);
              const isStartOfBlock = pos === $pos.start();

              // Check if cursor is actively inside an unclosed bracket [...] or brace {...} in this node
              const relCursor = cursorPos - pos;
              const textBeforeCursor = relCursor >= 0 && relCursor <= text.length ? text.substring(0, relCursor) : '';
              const lastOpenBk = textBeforeCursor.lastIndexOf('[');
              const lastCloseBk = textBeforeCursor.lastIndexOf(']');
              const isInsideActiveBk = lastOpenBk !== -1 && lastOpenBk > lastCloseBk;

              // Helper to check if cursor is actively typing inside a token range
              const isActivelyTyping = (from, to) => {
                if (forceValidate) return false;
                const inside = isSelectionEmpty && cursorPos >= from - 1 && cursorPos <= to + 1;
                const recent = now - lastEditTimestamp < 500;
                return inside && recent;
              };

              // Scan for all syntax errors (stray delimiters, malformed variables, commands, brackets)
              const errors = scanTextForSyntaxErrors(text, isStartOfBlock);

              errors.forEach((err) => {
                const errFrom = pos + err.from;
                const errTo = pos + err.to;

                // While user is still inside active bracket, suppress partial condition errors
                if (isInsideActiveBk && isSelectionEmpty && relCursor >= lastOpenBk && err.from >= lastOpenBk) {
                  return;
                }

                if (!isActivelyTyping(errFrom, errTo)) {
                  decos.push(
                    Decoration.inline(errFrom, errTo, {
                      class: `syntax-error-highlight ${err.severity === 'warning' ? 'syntax-warning' : ''}`,
                      title: err.message,
                    })
                  );
                }
              });

              // Condition live analysis while user types inside [ ... ]
              const bracketMatch = /\[([^\]\n]*)\]?/g;
              let bm;
              while ((bm = bracketMatch.exec(text)) !== null) {
                const condContent = bm[1];
                const condFrom = pos + bm.index + 1;
                const condTo = condFrom + condContent.length;

                // If cursor is actively typing inside this condition bracket:
                if (isSelectionEmpty && cursorPos >= condFrom && cursorPos <= condTo) {
                  const analysis = analyzeConditionText(condContent);
                  if (analysis.isValid && analysis.summary) {
                    // Valid condition live indicator
                    decos.push(
                      Decoration.inline(condFrom, condTo, {
                        class: 'syntax-condition-valid',
                        title: analysis.summary,
                      })
                    );
                  }
                }
              }
            });

            return {
              decorations: DecorationSet.create(newEditorState.doc, decos),
              lastValidatedDoc: newEditorState.doc,
            };
          },
        },

        props: {
          decorations(state) {
            return this.getState(state)?.decorations || DecorationSet.empty;
          },

          // ── 1. Auto-Closing Delimiters & Over-Typing ──────────────────────────
          handleTextInput(view, from, to, text) {
            const { state } = view;
            const { doc, selection } = state;

            // 1a. Auto-close for single '<' and double '<<'
            if (text === '<') {
              if (!selection.empty) {
                const selectedText = doc.textBetween(from, to);
                // If already enclosed in <...>, expand to <<...>>
                if (selectedText.startsWith('<') && selectedText.endsWith('>')) {
                  const inner = selectedText.slice(1, -1);
                  const tr = state.tr.replaceWith(
                    from,
                    to,
                    state.schema.text('<<' + inner + '>>')
                  );
                  view.dispatch(tr);
                  return true;
                }
                const tr = state.tr.replaceWith(
                  from,
                  to,
                  state.schema.text('<<' + selectedText + '>>')
                );
                view.dispatch(tr);
                return true;
              }

              const charBefore = from > 0 ? doc.textBetween(from - 1, from) : '';
              const charAfter =
                from < doc.content.size ? doc.textBetween(from, from + 1) : '';

              if (charBefore === '<' && charAfter === '>') {
                // Cursor is at <|>, user types '<' -> expand to <<|>>
                const tr = state.tr;
                tr.insertText('<', from);
                tr.insertText('>', from + 1);
                tr.setSelection(TextSelection.create(tr.doc, from + 1));
                view.dispatch(tr);
                return true;
              }

              // If next char is alphanumeric (user is prefixing an existing word, e.g. |Borrower),
              // just insert single '<' without closing '>' so user can type <<Borrower>> naturally
              if (charAfter && /[a-zA-Z0-9_\u00C0-\u024F]/.test(charAfter)) {
                return false;
              }

              // Single '<' typed: insert '<>' and position cursor between
              const tr = state.tr.insertText('<>', from);
              tr.setSelection(TextSelection.create(tr.doc, from + 1));
              view.dispatch(tr);
              return true;
            }

            // 1b. Auto-close for '{'
            if (text === '{') {
              if (!selection.empty) {
                const selectedText = doc.textBetween(from, to);
                const tr = state.tr.replaceWith(
                  from,
                  to,
                  state.schema.text('{' + selectedText + '}')
                );
                tr.setSelection(TextSelection.create(tr.doc, from + 1, to + 1));
                view.dispatch(tr);
                return true;
              }

              const charAfter =
                from < doc.content.size ? doc.textBetween(from, from + 1) : '';
              if (charAfter && /[a-zA-Z0-9_\u00C0-\u024F]/.test(charAfter)) {
                return false;
              }

              const tr = state.tr.insertText('{}', from);
              tr.setSelection(TextSelection.create(tr.doc, from + 1));
              view.dispatch(tr);
              return true;
            }

            // 1c. Auto-close for '['
            if (text === '[') {
              if (!selection.empty) {
                const selectedText = doc.textBetween(from, to);
                const tr = state.tr.replaceWith(
                  from,
                  to,
                  state.schema.text('[' + selectedText + ']')
                );
                tr.setSelection(TextSelection.create(tr.doc, from + 1, to + 1));
                view.dispatch(tr);
                return true;
              }

              const charAfter =
                from < doc.content.size ? doc.textBetween(from, from + 1) : '';
              if (charAfter && /[a-zA-Z0-9_\u00C0-\u024F]/.test(charAfter)) {
                return false;
              }

              const tr = state.tr.insertText('[]', from);
              tr.setSelection(TextSelection.create(tr.doc, from + 1));
              view.dispatch(tr);
              return true;
            }

            // 1d. Over-typing closing delimiter '>'
            if (text === '>') {
              if (selection.empty && from < doc.content.size) {
                const nextChar = doc.textBetween(from, from + 1);
                const nextTwo = from + 2 <= doc.content.size ? doc.textBetween(from, from + 2) : '';

                // If cursor is right before '>>', step over both and trigger conversion
                if (nextTwo === '>>') {
                  const newPos = from + 2;
                  const tr = state.tr.setSelection(
                    TextSelection.create(doc, newPos)
                  );
                  view.dispatch(tr);
                  return true;
                }

                if (nextChar === '>') {
                  const newPos = from + 1;
                  const tr = state.tr.setSelection(
                    TextSelection.create(doc, newPos)
                  );
                  view.dispatch(tr);
                  return true;
                }
              }
            }

            // 1e. Over-typing closing delimiter ']'
            if (text === ']') {
              if (selection.empty && from < doc.content.size) {
                const nextChar = doc.textBetween(from, from + 1);
                if (nextChar === ']') {
                  const tr = state.tr.setSelection(
                    TextSelection.create(doc, from + 1)
                  );
                  tr.setMeta(smartSyntaxPluginKey, { forceCheckBraces: true });
                  view.dispatch(tr);
                  return true;
                }
              }
            }

            // 1f. Over-typing closing delimiter '}'
            if (text === '}') {
              if (selection.empty && from < doc.content.size) {
                const nextChar = doc.textBetween(from, from + 1);
                if (nextChar === '}') {
                  const newPos = from + 1;
                  const tr = state.tr.setSelection(
                    TextSelection.create(doc, newPos)
                  );
                  tr.setMeta(smartSyntaxPluginKey, { forceCheckBraces: true });
                  view.dispatch(tr);
                  return true;
                }
              }
            }

            return false;
          },

          // ── 2. Backspace & Delete Behavior for Delimiter Pairs ────────────────
          handleKeyDown(view, event) {
            const { state } = view;
            const { selection, doc } = state;

            if (event.key === 'Backspace' && selection.empty) {
              const { from } = selection;
              if (from > 0 && from < doc.content.size) {
                const charBefore = doc.textBetween(from - 1, from);
                const charAfter = doc.textBetween(from, from + 1);

                if (
                  (charBefore === '<' && charAfter === '>') ||
                  (charBefore === '{' && charAfter === '}') ||
                  (charBefore === '[' && charAfter === ']')
                ) {
                  event.preventDefault();
                  const tr = state.tr.delete(from - 1, from + 1);
                  view.dispatch(tr);
                  return true;
                }
              }
            }

            if (event.key === 'Delete' && selection.empty) {
              const { from } = selection;
              if (from < doc.content.size - 1) {
                const char1 = doc.textBetween(from, from + 1);
                const char2 = doc.textBetween(from + 1, from + 2);

                if (
                  (char1 === '<' && char2 === '>') ||
                  (char1 === '{' && char2 === '}') ||
                  (char1 === '[' && char2 === ']')
                ) {
                  event.preventDefault();
                  const tr = state.tr.delete(from, from + 2);
                  view.dispatch(tr);
                  return true;
                }
              }
            }

            return false;
          },
        },

        // ── 3. AppendTransaction: Auto-Convert Commands, Conditions, & Variables ──
        appendTransaction(transactions, oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged || tr.selectionSet)) return null;

          // Do not re-convert on undo/redo actions
          const isHistory = transactions.some((tr) => tr.getMeta('history$'));
          if (isHistory) return null;

          // ── 3a. Page Break & Line Commands ─────────────────────────────────
          let cmdMatch = null;
          newState.doc.descendants((node, pos) => {
            if (cmdMatch) return false;
            if (!node.isText) return;
            const text = node.text || '';

            const pbMatch = /(?<!<)<page\s+break>(?!>)/i.exec(text);
            if (pbMatch) {
              cmdMatch = {
                from: pos + pbMatch.index,
                to: pos + pbMatch.index + pbMatch[0].length,
                type: 'pageBreak',
              };
              return false;
            }

            const lineMatch = /(?<!<)<line>(?!>)/i.exec(text);
            if (lineMatch) {
              cmdMatch = {
                from: pos + lineMatch.index,
                to: pos + lineMatch.index + lineMatch[0].length,
                type: 'horizontalLineBlock',
              };
              return false;
            }
          });

          if (cmdMatch) {
            const tr = newState.tr;
            const nodeType = newState.schema.nodes[cmdMatch.type];
            if (nodeType) {
              const $from = tr.doc.resolve(cmdMatch.from);
              const parent = $from.parent;
              const isOnlyChild =
                parent.isTextblock &&
                parent.textContent.trim().toLowerCase() ===
                  (cmdMatch.type === 'pageBreak' ? '<page break>' : '<line>');
              const isInTableCell =
                $from.depth > 1 &&
                $from.node($from.depth - 1)?.type.name.toLowerCase().includes('table');
              const isAtDocEnd = $from.after() >= tr.doc.content.size - 1;
              const blockNode = nodeType.create();
              const pType = newState.schema.nodes.paragraph;

              if (isOnlyChild && $from.depth > 0) {
                const pStart = $from.before();
                const pEnd = $from.after();
                if (isInTableCell || isAtDocEnd) {
                  tr.replaceWith(pStart, pEnd, [blockNode, pType.create()]);
                  tr.setSelection(
                    TextSelection.near(tr.doc.resolve(pStart + blockNode.nodeSize + 1))
                  );
                } else {
                  tr.replaceWith(pStart, pEnd, blockNode);
                  tr.setSelection(
                    TextSelection.near(
                      tr.doc.resolve(Math.min(pStart + blockNode.nodeSize, tr.doc.content.size))
                    )
                  );
                }
              } else {
                tr.replaceWith(cmdMatch.from, cmdMatch.to, blockNode);
                tr.setSelection(
                  TextSelection.near(
                    tr.doc.resolve(Math.min(cmdMatch.from + blockNode.nodeSize, tr.doc.content.size))
                  )
                );
              }
              return tr;
            }
          }

          // ── 3a. Upgrade existing optionalBlock to conditionalBlock if it contains [Condition] ──
          let optUpgrade = null;
          newState.doc.descendants((node, pos) => {
            if (optUpgrade) return false;
            if (!node.isInline || !node.isText) return;
            const optMark = node.marks?.find((m) => m.type.name === 'optionalBlock');
            if (!optMark) return;
            const text = node.text || '';
            const bkMatch = /\[([^\]\n]+)\]/.exec(text);
            if (bkMatch && isConditionBracket(bkMatch[1])) {
              const condText = bkMatch[1].trim();
              const cursorPos = newState.selection.from;
              const isSelectionEmpty = newState.selection.empty;
              const bkFrom = pos + bkMatch.index;
              const bkTo = bkFrom + bkMatch[0].length;
              // Do not convert while user is still actively typing inside [ ... ]
              if (isSelectionEmpty && cursorPos > bkFrom && cursorPos < bkTo) {
                return;
              }
              optUpgrade = {
                pos,
                nodeSize: node.nodeSize,
                condText,
                bkFrom,
                bkTo,
                optMark,
              };
            }
          });

          if (optUpgrade && newState.schema.marks.conditionalBlock) {
            const tr = newState.tr;
            const parsed = parseConditionString(optUpgrade.condText);
            const ruleId = 'rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
            const cleanLabel = optUpgrade.condText
              .replace(/^condition\s*:\s*/i, '')
              .replace(/\s+EXISTS\b/gi, '')
              .replace(/\s+exists\b/gi, '')
              .replace(/<<|>>/g, '')
              .trim();

            const condMark = newState.schema.marks.conditionalBlock.create({
              ruleId,
              operator: parsed.operator,
              rules: parsed.rules,
              label: cleanLabel || 'Condition',
            });

            // 1. Delete the bracket [Condition]
            tr.delete(optUpgrade.bkFrom, optUpgrade.bkTo);

            // 2. Remove the optionalBlock mark from this node range
            const newStart = optUpgrade.pos;
            const newEnd = tr.mapping.map(optUpgrade.pos + optUpgrade.nodeSize);
            tr.removeMark(newStart, newEnd, optUpgrade.optMark.type);

            // 3. Add the conditionalBlock mark to the remaining content
            if (newEnd > newStart) {
              tr.addMark(newStart, newEnd, condMark);
            } else {
              tr.insertText('\u00A0', newStart);
              tr.addMark(newStart, newStart + 1, condMark);
            }
            tr.removeStoredMark(condMark.type);
            tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(newStart + 1, newEnd))));
            return tr;
          }

          // ── 3b. Conditional Blocks ({Statement [Condition]} or {[Condition] Statement}) & Optional Blocks ─
          let braceMatch = null;
          newState.doc.descendants((node, pos) => {
            if (braceMatch) return false;
            if (!node.isTextblock) return;

            let text = '';
            const posMap = [];

            node.forEach((child, offset) => {
              const childPos = pos + 1 + offset;
              if (child.isText) {
                const t = child.text || '';
                for (let i = 0; i < t.length; i++) {
                  posMap.push(childPos + i);
                  text += t[i];
                }
              } else if (child.type.name === 'variableTag') {
                const vText = `<<${child.attrs.label || child.attrs.variableKey}>>`;
                for (let i = 0; i < vText.length; i++) {
                  posMap.push(childPos);
                  text += vText[i];
                }
              }
            });

            if (!text.includes('{') || !text.includes('}')) return;

            const braceRegex = /\{([^{}]+)\}/g;
            let bm;
            while ((bm = braceRegex.exec(text)) !== null) {
              const openIdx = bm.index;
              const closeIdx = bm.index + bm[0].length - 1;
              const inner = bm[1];
              const openBracePos = posMap[openIdx];
              const closeBracePos = posMap[closeIdx];
              if (openBracePos === undefined || closeBracePos === undefined) continue;

              const cursorPos = newState.selection.from;
              const isSelectionEmpty = newState.selection.empty;

              // Check if inner contains a condition bracket [ ... ]
              const bkMatch = /\[([^\]\n]+)\]/.exec(inner);
              if (bkMatch && isConditionBracket(bkMatch[1])) {
                const bkStartIdx = openIdx + 1 + bkMatch.index;
                const bkEndIdx = bkStartIdx + bkMatch[0].length;
                const bracketStartPos = posMap[bkStartIdx];
                const bracketEndPos = posMap[bkEndIdx - 1] + 1;

                // If user is actively typing strictly inside [ ... ], wait until they leave or close it
                if (isSelectionEmpty && cursorPos > bracketStartPos && cursorPos < bracketEndPos) {
                  continue;
                }

                braceMatch = {
                  openBracePos,
                  closeBracePos,
                  bracketStartPos,
                  bracketEndPos,
                  condText: bkMatch[1].trim(),
                  isConditional: true,
                };
                return false;
              } else {
                // If there is an unclosed '[' inside '{ ... }', do not convert to optionalBlock yet
                if (inner.includes('[')) {
                  continue;
                }
                // If user is actively typing inside { ... }, do not convert to optionalBlock yet
                if (isSelectionEmpty && cursorPos > openBracePos && cursorPos < closeBracePos) {
                  continue;
                }

                braceMatch = {
                  openBracePos,
                  closeBracePos,
                  isConditional: false,
                };
                return false;
              }
            }
          });

          if (braceMatch) {
            const tr = newState.tr;
            if (braceMatch.isConditional && newState.schema.marks.conditionalBlock) {
              const parsed = parseConditionString(braceMatch.condText);
              const ruleId = 'rule_' + Date.now() + '_' + Math.random().toString(36).substring(7);
              const cleanLabel = braceMatch.condText
                .replace(/^condition\s*:\s*/i, '')
                .replace(/\s+EXISTS\b/gi, '')
                .replace(/\s+exists\b/gi, '')
                .replace(/<<|>>/g, '')
                .trim();

              const condMark = newState.schema.marks.conditionalBlock.create({
                ruleId,
                operator: parsed.operator,
                rules: parsed.rules,
                label: cleanLabel || 'Condition',
              });

              // Delete in reverse position order: '}', then '[Condition]', then '{'
              tr.delete(braceMatch.closeBracePos, braceMatch.closeBracePos + 1);
              tr.delete(braceMatch.bracketStartPos, braceMatch.bracketEndPos);
              tr.delete(braceMatch.openBracePos, braceMatch.openBracePos + 1);

              const newStart = braceMatch.openBracePos;
              const newEnd = tr.mapping.map(braceMatch.closeBracePos);

              if (newEnd > newStart) {
                tr.addMark(newStart, newEnd, condMark);
              } else {
                tr.insertText('\u00A0', newStart);
                tr.addMark(newStart, newStart + 1, condMark);
              }
              tr.removeStoredMark(condMark.type);
              tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(newStart + 1, newEnd))));
              return tr;
            } else if (!braceMatch.isConditional && newState.schema.marks.optionalBlock) {
              const optMark = newState.schema.marks.optionalBlock.create();
              tr.delete(braceMatch.closeBracePos, braceMatch.closeBracePos + 1);
              tr.delete(braceMatch.openBracePos, braceMatch.openBracePos + 1);

              const newStart = braceMatch.openBracePos;
              const newEnd = tr.mapping.map(braceMatch.closeBracePos);

              if (newEnd > newStart) {
                tr.addMark(newStart, newEnd, optMark);
              } else {
                tr.insertText('\u00A0', newStart);
                tr.addMark(newStart, newStart + 1, optMark);
              }
              tr.removeStoredMark(optMark.type);
              tr.setSelection(TextSelection.near(tr.doc.resolve(Math.max(newStart + 1, newEnd))));
              return tr;
            }
          }

          // ── 3c. Variables (<<Variable Name>>) ────────────────────────────────
          let varMatch = null;
          newState.doc.descendants((node, pos) => {
            if (varMatch) return false;
            if (!node.isText) return;
            const text = node.text || '';

            const regex = /(?:\*\*<<|<<\*\*|<<|«\*\*|«)([^<>«»\n]+?)(?:\*\*>>|>>\*\*|>>|»\*\*|»)/g;
            let vm;
            while ((vm = regex.exec(text)) !== null) {
              // Ensure this variable is not inside an unclosed condition bracket [...]
              const textBefore = text.substring(0, vm.index);
              const lastOpen = textBefore.lastIndexOf('[');
              const lastClose = textBefore.lastIndexOf(']');
              const isInsideBracket = lastOpen !== -1 && lastOpen > lastClose;
              if (isInsideBracket) continue;

              const fullMatch = vm[0];
              const isBold = fullMatch.includes('**');
              const rawName = vm[1].replace(/\*\*/g, '').trim();
              if (!rawName) continue;

              const matchFrom = pos + vm.index;
              const matchTo = pos + vm.index + fullMatch.length;

              // If user is actively typing inside << ... >>, do not convert yet
              const cursorPos = newState.selection.from;
              const isSelectionEmpty = newState.selection.empty;
              if (isSelectionEmpty && cursorPos > matchFrom && cursorPos < matchTo) {
                continue;
              }

              varMatch = {
                from: matchFrom,
                to: matchTo,
                rawName,
                isBold,
              };
              return false;
            }
          });

          if (varMatch) {
            const varType = newState.schema.nodes.variableTag;
            if (varType) {
              const key = varMatch.rawName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
              const marks =
                varMatch.isBold && newState.schema.marks.bold
                  ? [newState.schema.marks.bold.create()]
                  : [];
              const varNode = varType.create(
                {
                  variableKey: key,
                  label: varMatch.rawName,
                },
                null,
                marks
              );

              const tr = newState.tr;
              tr.replaceWith(varMatch.from, varMatch.to, varNode);
              tr.setSelection(
                TextSelection.near(
                  tr.doc.resolve(Math.min(varMatch.from + varNode.nodeSize, tr.doc.content.size))
                )
              );
              return tr;
            }
          }

          return null;
        },

        // ── 4. View Lifecycle: Debounced Validation Trigger ─────────────────────
        view(editorView) {
          return {
            update(view, prevState) {
              if (
                view.state.docChanged ||
                !prevState.selection.eq(view.state.selection)
              ) {
                if (debounceTimer) clearTimeout(debounceTimer);
                debounceTimer = setTimeout(() => {
                  if (view.isDestroyed) return;
                  const tr = view.state.tr.setMeta(smartSyntaxPluginKey, {
                    forceValidate: true,
                  });
                  view.dispatch(tr);
                }, 400);
              }
            },
            destroy() {
              if (debounceTimer) clearTimeout(debounceTimer);
            },
          };
        },
      }),
    ];
  },
});

export default SmartSyntaxExtension;
